/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Schema, ValidationError } from '@squiz/component-editing-ui';
import { NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  ALERT_TYPE_INFO,
  ALERT_TYPE_REPORT,
  Alert,
  AlertProps,
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_SUCCESS,
  Button,
  ButtonList,
  CheckboxRadioProps,
  CronEditor,
  CronEditorProps,
  DatetimePicker,
  DatetimePickerProps,
  FormControlOption,
  PICKER_DATETIME_FORMAT,
  RadioButtons,
  Row,
  SPINNER_TYPE_REGULAR,
  SelectField,
  SelectFieldOption,
  SelectFieldProps,
  SingleDatetimePickerResult,
  Spinner,
  TEXTFIELD_TYPE_MULTIPLE,
  TEXTFIELD_TYPE_PASSWORD,
  TEXTFIELD_TYPE_TEXTAREA,
  TOAST_TYPE_DANGER,
  TOAST_TYPE_SUCCESS,
  TextField,
  TextFieldProps,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { JsonEditor } from 'apps/console-core/src/Components/JsonEditor/JsonEditor';
import { JsonEditorProps } from 'apps/console-core/src/Components/JsonEditor/JsonEditor.props';
import { EntityDxpUserMeOrganisation } from 'apps/console-core/src/Services/console';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { useConsoleServiceStore, useToastStore } from 'dxpConsoleCore-mf/State';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PeriodType } from 'react-js-cron';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import {
  EntityEventSubscriptionRuleCreated,
  EntityEventSubscriptionRuleUpdated,
  EventBusCreateEventSubscriptionTargetDto,
  EventBusCreateScheduleDto,
  EventBusCreateSubscriptionRuleDto,
  EventBusSubscriptionTargetType,
  EventBusUpdateEventSubscriptionTargetDto,
  EventBusUpdateSubscriptionRuleDto,
  EventRuleSyntax,
  EventSchedulerType,
  getEventBusService,
} from '../../../../Services/event-bus';
import { EntityEventScheduleCreated } from '../../../../Services/event-bus/src/Entities/EventScheduleCreated/EventScheduleCreated.entity';
import {
  EntityJob,
  EntityJobContext,
  EntityJobManifest,
  JobRunnerServiceBeginExecutionDto,
  getJobRunnerService,
} from '../../../../Services/job-runner';
import { getEventSubscriptionPath } from '../../../../Utilities/GetEventSubscriptionPath/GetEventSubscriptionPath';
import { JobManifestInputSchema } from '../../../JobExecutions/forms/StartJobForm/StartJobForm.types';
import { EventSubscriptionAttributes } from '../../elements/EventSubscriptionAttributes/EventSubscriptionAttributes';
import { EventSubscriptionMenu } from '../../elements/EventSubscriptionMenu/EventSubscriptionMenu';
import { convertToAttributeFilter } from '../../utils/ConvertToAttributeFilter/ConvertToAttributeFilter';
import { convertToEventPattern } from '../../utils/ConvertToEventPattern/ConvertToEventPattern';
import { getJobRunnerTarget } from '../../utils/GetJobRunnerTarget/GetJobRunnerTarget';
import { isJson } from '../../utils/IsJson/IsJson';
import { EventSubscriptionNameValidator } from '../../validators/EventSubscriptionNameValidator/EventSubscriptionNameValidator.support';
import { TargetUrlValidator } from '../../validators/TargetUrlValidator/TargetUrlValidator.support';
import {
  AU_CONNECT_WEBHOOK_URL,
  DUMMY_KEY_NO_AUTH_REQUIRED,
  EVENT_SUBSCRIPTION_BUILDER_DATA_IDS,
  EVENT_SUBSCRIPTION_BUILDER_FORM_IDS,
  EVENT_TRANSFORMER_FORM_IDS,
  JOB_RUNNER_EXECUTIONS_ENDPOINT,
  JOB_RUNNER_URL_PATH,
  US_CONNECT_WEBHOOK_URL,
} from './EventSubscriptionBuilderForm.constants';
import {
  AttributeFilter,
  AttributeRule,
  EventScheduledJobInputFormType,
  EventSubscriptionEventType,
  EventSubscriptionSource,
  EventSubscriptionSourceAll,
  EventSubscriptionTargetType,
} from './EventSubscriptionBuilderForm.defs';
import {
  DEFAULT_EVENT_TRANSFORMER_EVENT_PATHS_MAP,
  DEFAULT_EVENT_TRANSFORMER_EVENT_TEMPLATE_STRING,
  EVENT_SCHEDULER_TYPE_OPTIONS,
  EVENT_SUBSCRIPTION_TARGET_TYPE_OPTIONS,
  EVENT_SUBSCRIPTION_TARGET_TYPE_SCHEDULER_OPTIONS,
  EVENT_TYPE_OPTIONS,
  SUBSCRIPTION_SOURCE_OPTIONS,
} from './EventSubscriptionBuilderForm.fixture';
import { EventSubscriptionBuilderFormProps } from './EventSubscriptionBuilderForm.props';

/**
 * The form for creating an event subscription.
 */
export const EventSubscriptionBuilderForm = (props: EventSubscriptionBuilderFormProps): JSX.Element => {
  const { t } = useTranslation();
  const { eventSchedule, eventSubscription, subscriptionId } = props;

  // Prepare variables if form is being used for updating
  let subscriptionName = '';
  let targetName = '';

  if (eventSubscription) {
    subscriptionName = eventSubscription.name;
    if (eventSubscription.targets) {
      targetName = eventSubscription.targets[0].name;
    }
  }

  const { tenant }: { tenant: EntityDxpUserMeOrganisation } = useConsoleServiceStore();
  const { updateActiveToast } = useToastStore();

  const eventBusService = getEventBusService();
  const jobRunnerService = getJobRunnerService();

  // Handle navigation
  const navigate = useNavigate();
  const eventSubscriptionsPath = useMemo(() => {
    return getCapabilityPath(`/event-service${getEventSubscriptionPath()}`);
  }, []);

  const returnToEventSubscriptionsList = useCallback((): void => {
    navigate(eventSubscriptionsPath);
  }, [eventSubscriptionsPath, navigate]);

  //--------------------
  // States
  //--------------------

  const [isLoaded, setLoaded] = useState(false); // Assists with loading data into the form
  const [isFormSubmitted, setSubmit] = useState(false);
  const [isMakingRequest, setRequest] = useState(false); // Form has been validated
  const [isFormError, setFormError] = useState(false); // Form errors outside of JsonEditor
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  // Stores the representation of all the attribute filters provided by the user
  const [attributeFilters, setAttributeFilters] = useState<Array<AttributeFilter>>([]);

  const [name, setName] = useState(``);
  const [source, setSource] = useState<EventSubscriptionSourceAll>();
  const [eventType, setEventType] = useState<EventSubscriptionEventType | null>(null);

  const [isEventScheduler, setEventScheduler] = useState(!!eventSchedule);
  const [schedulerType, setSchedulerType] = useState<EventSchedulerType>(
    eventSchedule && eventSchedule.scheduleExpression.startsWith('cron(')
      ? EventSchedulerType.recurring
      : EventSchedulerType.once,
  );

  // Schedule states
  const [defaultCronPeriod, setCron] = useState<PeriodType | undefined>(undefined);
  const [cronValue, setCronValue] = useState('');
  const [datetime, setDatetime] = useState<number | null>(null);

  // Scheduled integration flow input
  const [flowInput, setFlowInput] = useState(``);

  // Scheduled job input / JsonEditor
  const [validate, setValidate] = useState(false);
  const [jobInputData, setJobInputData] = useState<EventScheduledJobInputFormType | null>(null);
  const [jobManifest, setJobManifest] = useState<EntityJobManifest | null>(null);

  const [targetType, setTargetType] = useState(``);

  // API / integration flow
  const [isDefaultRowSize, setDefaultRowSize] = useState(false);
  const [targetUrl, setTargetUrl] = useState(``);
  const [isPasswordField, setPasswordField] = useState(subscriptionId !== undefined);
  const [targetApiKey, setTargetApiKey] = useState(``);

  // Event transformer
  const [eventPathsMap, setEventPathsMap] = useState(``);
  const [eventTemplate, setEventTemplate] = useState(``);

  // Job dropdown options
  const [errorFetchingJobs, setErrorFetchingJobs] = useState(false);
  const [errorFetchingContexts, setErrorFetchingContexts] = useState(false);
  const [jobList, setJobList] = useState<Array<EntityJob> | null>(null);
  const [contextList, setContextList] = useState<Array<EntityJobContext> | null>(null);
  const [selectedJob, setSelectedJob] = useState<string>();
  const [selectedContext, setSelectedContext] = useState<string>();

  //--------------------
  // Refs
  //--------------------

  const nameInputRef = useRef<HTMLInputElement>(null);
  const sourceInputRef = useRef<HTMLInputElement>(null);
  const eventTypeInputRef = useRef<HTMLInputElement>(null);

  const targetTypeInputRef = useRef<HTMLInputElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const apiKeyInputRef = useRef<HTMLInputElement>(null);

  const schedulerButtonsRef = useRef<HTMLInputElement>(null);
  const flowInputRef = useRef<HTMLInputElement>(null);
  const eventPathsMapInputRef = useRef<HTMLInputElement>(null);
  const eventTemplateInputRef = useRef<HTMLInputElement>(null);

  const jobNameInputRef = useRef<HTMLInputElement>(null);
  const contextNameInputRef = useRef<HTMLInputElement>(null);

  //--------------------
  // Labels
  //--------------------

  const labels = useMemo(() => {
    return {
      apiEventTransformerDescription: t(CONFIGURATION_STRINGS.apiEventTransformerDescription),
      apiKey: t(CONFIGURATION_STRINGS.apiKey),
      apiKeyDetails: t(CONFIGURATION_STRINGS.apiKeyDetails),
      cancel: t(CONFIGURATION_STRINGS.cancel),
      context: t(CONFIGURATION_STRINGS.startJobContextLabel),
      create: t(CONFIGURATION_STRINGS.create),
      datetimePickerPlaceholder: t(CONFIGURATION_STRINGS.datetimePickerPlaceholder),
      errorMessageWhenJobOrContextFetchFails: t(CONFIGURATION_STRINGS.errorMessageWhenJobOrContextFetchFails),
      eventPathsMap: t(CONFIGURATION_STRINGS.eventPathsMap),
      eventPathsMapPlaceholder: t(CONFIGURATION_STRINGS.eventPathsMapPlaceholder),
      eventTemplate: t(CONFIGURATION_STRINGS.eventTemplate),
      eventTemplatePlaceholder: t(CONFIGURATION_STRINGS.eventTemplatePlaceholder),
      eventTransformerOptional: t(CONFIGURATION_STRINGS.eventTransformerOptional),
      eventTypeNote: t(CONFIGURATION_STRINGS.eventTypeNote),
      eventTypeRequired: t(CONFIGURATION_STRINGS.eventTypeRequired),
      flowInput: t(CONFIGURATION_STRINGS.flowInput),
      flowInputDescription: t(CONFIGURATION_STRINGS.flowInputDescription),
      flowInputPlaceholder: t(CONFIGURATION_STRINGS.flowInputPlaceholder),
      flowWebhookUrlRequired: t(CONFIGURATION_STRINGS.flowWebhookUrlRequired),
      incorrectFormatCriteria: t(CONFIGURATION_STRINGS.incorrectFormatCriteria),
      integrationsDetails: t(CONFIGURATION_STRINGS.integrationsDetails),
      integrationsDetailsAlertMessage: t(CONFIGURATION_STRINGS.integrationsDetailsAlertMessage),
      integrationsDetailsScheduledAlertMessage: t(CONFIGURATION_STRINGS.integrationsDetailsScheduledAlertMessage),
      integrationsEventTransformerDescription: t(CONFIGURATION_STRINGS.integrationsEventTransformerDescription),
      integrationsFlowTargetUrlHelperText: t(CONFIGURATION_STRINGS.integrationsFlowTargetUrlHelperText),
      invalidUrlError: t(CONFIGURATION_STRINGS.invalidUrlError),
      invalidUrlForApiTargetTypeError: t(CONFIGURATION_STRINGS.invalidUrlForApiTargetTypeError),
      invalidUrlForIntegrationsFlowTargetTypeError: t(
        CONFIGURATION_STRINGS.invalidUrlForIntegrationsFlowTargetTypeError,
      ),
      jobDetails: t(CONFIGURATION_STRINGS.jobDetails),
      jobDetailsAlertMessage: t(CONFIGURATION_STRINGS.jobDetailsAlertMessage),
      jobDetailsScheduledAlertMessage: t(CONFIGURATION_STRINGS.jobDetailsScheduledAlertMessage),
      jobEventTransformerDescription: t(CONFIGURATION_STRINGS.jobEventTransformerDescription),
      jobInputs: t(CONFIGURATION_STRINGS.jobInputs),
      jobInputsDescription: t(CONFIGURATION_STRINGS.jobInputsDescription),
      jsonFormatNote: t(CONFIGURATION_STRINGS.jsonFormatNote),
      jsonObjectInput: t(CONFIGURATION_STRINGS.jsonObjectInput),
      nameRequired: t(CONFIGURATION_STRINGS.nameRequired),
      requiredField: t(CONFIGURATION_STRINGS.requiredField),
      save: t(CONFIGURATION_STRINGS.save),
      scheduler: t(CONFIGURATION_STRINGS.scheduler),
      schedulerOnceDescription: t(CONFIGURATION_STRINGS.schedulerOnceDescription),
      schedulerRecurringDescription: t(CONFIGURATION_STRINGS.schedulerRecurringDescription),
      schedulerType: t(CONFIGURATION_STRINGS.schedulerType),
      sourceNote: t(CONFIGURATION_STRINGS.sourceNote),
      sourceRequired: t(CONFIGURATION_STRINGS.sourceRequired),
      subscriptionDetails: t(CONFIGURATION_STRINGS.subscriptionDetails),
      subscriptionNameValidation: t(CONFIGURATION_STRINGS.subscriptionNameValidation),
      targetDescription: t(CONFIGURATION_STRINGS.targetDescription),
      targetDescriptionScheduled: t(CONFIGURATION_STRINGS.targetDescriptionScheduled),
      targetDetails: t(CONFIGURATION_STRINGS.targetDetails),
      targetTypeRequired: t(CONFIGURATION_STRINGS.typeRequired),
      toastCreateFailContent1: t(CONFIGURATION_STRINGS.eventSubscriptionCreatedFailToast1),
      toastCreateFailContent2: t(CONFIGURATION_STRINGS.eventSubscriptionCreatedFailToast2),
      toastCreateFailTitle: t(CONFIGURATION_STRINGS.error),
      toastCreateSuccessContent1: t(CONFIGURATION_STRINGS.eventSubscriptionCreatedSuccessToast1),
      toastCreateSuccessContent2: t(CONFIGURATION_STRINGS.eventSubscriptionCreatedSuccessToast2),
      toastCreateSuccessTitle: t(CONFIGURATION_STRINGS.eventSubscriptionCreated),
      toastUpdateFailTitle: t(CONFIGURATION_STRINGS.changesCouldNotBeSaved),
      toastUpdateSuccessTitle: t(CONFIGURATION_STRINGS.changesSaved),
      tryAgain: t(CONFIGURATION_STRINGS.tryAgain),
      urlRequired: t(CONFIGURATION_STRINGS.urlRequired),
      utcAlertMessage: t(CONFIGURATION_STRINGS.utcAlertMessage),
      utcDateAndTimeRequired: t(CONFIGURATION_STRINGS.utcDateAndTimeRequired),
    };
  }, [t]);

  //--------------------
  // Set up inputs
  //--------------------

  const inputs = {
    apiKey: apiKeyInputRef.current as HTMLInputElement,
    context: contextNameInputRef.current as HTMLInputElement,
    eventPathsMap: eventPathsMapInputRef.current as HTMLInputElement,
    eventTemplate: eventTemplateInputRef.current as HTMLInputElement,
    eventType: eventTypeInputRef.current as HTMLInputElement,
    flowInput: flowInputRef.current as HTMLInputElement,
    job: jobNameInputRef.current as HTMLInputElement,
    name: nameInputRef.current as HTMLInputElement,
    source: sourceInputRef.current as HTMLInputElement,
    targetType: targetTypeInputRef.current as HTMLInputElement,
    url: urlInputRef.current as HTMLInputElement,
  };

  //--------------------
  // Validators
  //--------------------

  const notEmptyValidator = useMemo(() => {
    return new NotEmptyValidator<boolean>((): boolean => false);
  }, []);

  const eventSubscriptionNameValidator = useMemo(() => {
    return new EventSubscriptionNameValidator<boolean>((): boolean => false);
  }, []);

  const urlValidator = useMemo(() => {
    return new TargetUrlValidator<boolean>((): boolean => false);
  }, []);

  //--------------------
  // Functions
  //--------------------

  const getDefaultAttributeFilter = useCallback((): AttributeFilter => {
    return {
      id: window.crypto.randomUUID(),
      name: '',
      rule: '' as AttributeRule,
      value: '',
    };
  }, []);

  // Set up dropdown options for job and job context
  const fetchJobsAndContexts = useCallback(async () => {
    setJobList(null);
    setContextList(null);

    // fetch jobs
    lastValueFrom(jobRunnerService.getJobs())
      .then((response) => {
        setJobList(response);
        setErrorFetchingJobs(false);
        return;
      })
      .catch((e) => {
        setJobList([]);
        setErrorFetchingJobs(true);
      });

    // fetch contexts
    lastValueFrom(jobRunnerService.getJobContexts())
      .then((response) => {
        setContextList(response);
        setErrorFetchingContexts(false);
        return;
      })
      .catch((e) => {
        setContextList([]);
        setErrorFetchingContexts(true);
      });
  }, [jobRunnerService]);

  // Check if url is a connect webhook flow url
  const connectWebhookUrlChecker = useCallback((url: string): boolean => {
    return url.startsWith(AU_CONNECT_WEBHOOK_URL) || url.startsWith(US_CONNECT_WEBHOOK_URL);
  }, []);

  // Event transformer is optional but if used, both inputs must be provided.
  const isEventTransformerRequired = useCallback((): boolean => {
    let nonEmptyInputs = 0;
    const eventTransformerInputs = EVENT_TRANSFORMER_FORM_IDS.map(
      (formId) => document.querySelector(`[id^=${formId}]`) as HTMLInputElement,
    ).filter((item) => item !== null);

    eventTransformerInputs.forEach((input) => {
      if (notEmptyValidator.validate(input.value)) {
        nonEmptyInputs++;
      }
    });

    return nonEmptyInputs > 0;
  }, [notEmptyValidator]);

  // Datetime picker callback
  const handleDatetime = (result: SingleDatetimePickerResult): void => {
    const nextErrors = { ...fieldErrors };
    delete nextErrors.datetime;

    setFieldErrors(nextErrors);
    setDatetime(result.date);
  };

  // Set up error messages for form fields
  const validateFields = useCallback(
    (inputsToCheck: Array<HTMLInputElement>, checkSchedule = false): void => {
      const nextErrors = { ...fieldErrors };

      inputsToCheck.forEach((input: HTMLInputElement): void => {
        const fieldId = input.getAttribute(`id`) as string;
        const isDisabledValue = fieldId.startsWith(`event-subscription-value-`) && input.disabled;
        delete nextErrors[fieldId];

        let isInValidApiUrl = false;
        if (fieldId === EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.url) {
          const isConnectWebhookUrl = connectWebhookUrlChecker(input.value);
          isInValidApiUrl = targetType === EventSubscriptionTargetType.Api && isConnectWebhookUrl;
        }

        let isInValidIntegrationsWebhookUrl = false;
        if (fieldId === EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.url) {
          const isConnectWebhookUrl = connectWebhookUrlChecker(input.value);
          isInValidIntegrationsWebhookUrl =
            targetType === EventSubscriptionTargetType.IntegrationsFlow && !isConnectWebhookUrl;
        }

        if (!isDisabledValue) {
          if (
            fieldId === EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionName &&
            notEmptyValidator.validate(input.value) &&
            !eventSubscriptionNameValidator.validate(input.value)
          ) {
            // Validate event subscription name
            nextErrors[fieldId] = labels.subscriptionNameValidation;
          } else if (
            fieldId === EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.url &&
            notEmptyValidator.validate(input.value) &&
            !urlValidator.validate(input.value.trim())
          ) {
            // Validate URL
            nextErrors[fieldId] = labels.invalidUrlError;
          } else if (isInValidApiUrl) {
            nextErrors[fieldId] = labels.invalidUrlForApiTargetTypeError;
          } else if (EVENT_TRANSFORMER_FORM_IDS.includes(fieldId)) {
            // Optional custom event transformer
            if (isEventTransformerRequired()) {
              if (!notEmptyValidator.validate(input.value)) {
                nextErrors[fieldId] = labels.requiredField;
              } else if (!isJson(input.value)) {
                nextErrors[fieldId] = labels.incorrectFormatCriteria;
              }
            }
          } else if (fieldId === EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.flowInput) {
            if (!notEmptyValidator.validate(input.value)) {
              // Input is optional
              delete nextErrors[fieldId];
            } else if (!isJson(input.value)) {
              nextErrors[fieldId] = labels.incorrectFormatCriteria;
            }
          } else if (!notEmptyValidator.validate(input.value)) {
            // Validate required fields
            nextErrors[fieldId] = labels.requiredField;
          } else if (isInValidIntegrationsWebhookUrl) {
            nextErrors[fieldId] = labels.invalidUrlForIntegrationsFlowTargetTypeError;
          }
        }
      });

      if (checkSchedule) {
        if (!datetime) {
          nextErrors.datetime = labels.requiredField;
        }

        if (!cronValue) {
          nextErrors.cron = labels.requiredField;
        }
      }

      setFieldErrors(nextErrors);
    },
    [
      connectWebhookUrlChecker,
      cronValue,
      datetime,
      eventSubscriptionNameValidator,
      fieldErrors,
      isEventTransformerRequired,
      labels.incorrectFormatCriteria,
      labels.invalidUrlError,
      labels.invalidUrlForApiTargetTypeError,
      labels.invalidUrlForIntegrationsFlowTargetTypeError,
      labels.requiredField,
      labels.subscriptionNameValidation,
      notEmptyValidator,
      targetType,
      urlValidator,
    ],
  );

  // Check for errors on change
  const errorChecker = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      validateFields([event.target]);
    },
    [validateFields],
  );

  // Check for errors for select fields
  const selectErrorChecker = useCallback(
    (formId: string) => {
      const nextErrors = { ...fieldErrors };
      delete nextErrors[formId];
      setFieldErrors(nextErrors);
    },
    [fieldErrors],
  );

  // Check for errors for the event transformer fields
  const eventTransformerErrorChecker = useCallback(() => {
    validateFields([
      eventPathsMapInputRef.current as HTMLInputElement,
      eventTemplateInputRef.current as HTMLInputElement,
    ]);
  }, [validateFields]);

  // Validate the form fields outside of the JsonEditor
  const validateSubmission = useCallback((): boolean => {
    // Mandatory fields to check
    const inputsToCheck = [inputs.name, inputs.source, inputs.targetType];

    if (targetType) {
      if (
        inputs.targetType.value === EventSubscriptionTargetType.Api ||
        inputs.targetType.value === EventSubscriptionTargetType.IntegrationsFlow
      ) {
        inputsToCheck.push(inputs.url);
      }

      if (inputs.targetType.value === EventSubscriptionTargetType.Job) {
        inputsToCheck.push(inputs.job);
        inputsToCheck.push(inputs.context);
      }
    }

    let hasAttributeFilterError = false;
    if (isEventScheduler) {
      if (inputs.targetType.value === EventSubscriptionTargetType.IntegrationsFlow) {
        inputsToCheck.push(inputs.flowInput);
      }
    } else {
      inputsToCheck.push(inputs.eventType);

      if (inputs.eventType.value) {
        inputsToCheck.push(inputs.eventPathsMap);
        inputsToCheck.push(inputs.eventTemplate);
      }

      // Handle validation of attribute filters on submission
      const attributeFilterInputs = document.querySelectorAll(
        `.event-subscription-attribute-filters__rows input`,
      ) as NodeListOf<HTMLInputElement>;

      hasAttributeFilterError = Array.from(attributeFilterInputs).some((attributeFilterInput) => {
        const fieldId = attributeFilterInput.getAttribute(`id`) as string;

        const isDisabledValue = fieldId.startsWith(`event-subscription-value-`) && attributeFilterInput.disabled;
        if (isDisabledValue) {
          return false;
        }

        if (!notEmptyValidator.validate(attributeFilterInput.value)) {
          return true;
        }

        return false;
      });

      if (hasAttributeFilterError) {
        inputsToCheck.push(...attributeFilterInputs);
      }
    }

    if (
      hasAttributeFilterError ||
      !eventSubscriptionNameValidator.validate(inputs.name.value) ||
      !notEmptyValidator.validate(inputs.name.value) ||
      !notEmptyValidator.validate(inputs.source.value) ||
      (!isEventScheduler && !notEmptyValidator.validate(inputs.eventType.value)) ||
      !notEmptyValidator.validate(inputs.targetType.value)
    ) {
      validateFields(inputsToCheck, isEventScheduler);
      setSubmit(false);
      return false;
    }

    if (targetType === EventSubscriptionTargetType.Api || targetType === EventSubscriptionTargetType.IntegrationsFlow) {
      const isConnectWebhookUrl = connectWebhookUrlChecker(inputs.url.value);

      if (
        (targetType === EventSubscriptionTargetType.Api && isConnectWebhookUrl) ||
        (targetType === EventSubscriptionTargetType.IntegrationsFlow && !isConnectWebhookUrl) ||
        !urlValidator.validate(inputs.url.value) ||
        !notEmptyValidator.validate(inputs.url.value) ||
        // Event transformer is optional. Either none or both inputs must be provided
        (!isEventScheduler &&
          ((!notEmptyValidator.validate(inputs.eventPathsMap.value) &&
            notEmptyValidator.validate(inputs.eventTemplate.value)) ||
            (notEmptyValidator.validate(inputs.eventPathsMap.value) &&
              !notEmptyValidator.validate(inputs.eventTemplate.value)) ||
            (inputs.eventPathsMap.value && !isJson(inputs.eventPathsMap.value)) ||
            (inputs.eventTemplate.value && !isJson(inputs.eventTemplate.value)))) ||
        (isEventScheduler && flowInput && !isJson(flowInput))
      ) {
        validateFields(inputsToCheck, isEventScheduler);
        setSubmit(false);
        return false;
      }
    }

    if (targetType === EventSubscriptionTargetType.Job) {
      if (
        !notEmptyValidator.validate(inputs.job.value) ||
        !notEmptyValidator.validate(inputs.context.value) ||
        // Event transformer is optional. Either none or both inputs must be provided
        (!isEventScheduler &&
          ((!notEmptyValidator.validate(inputs.eventPathsMap.value) &&
            notEmptyValidator.validate(inputs.eventTemplate.value)) ||
            (notEmptyValidator.validate(inputs.eventPathsMap.value) &&
              !notEmptyValidator.validate(inputs.eventTemplate.value)) ||
            (inputs.eventPathsMap.value && !isJson(inputs.eventPathsMap.value)) ||
            (inputs.eventTemplate.value && !isJson(inputs.eventTemplate.value))))
      ) {
        validateFields(inputsToCheck, isEventScheduler);
        setSubmit(false);
        return false;
      }
    }

    if (isEventScheduler) {
      validateFields([], true);

      if (schedulerType === EventSchedulerType.once && !datetime) {
        return false;
      }

      /* istanbul ignore next */
      if (schedulerType === EventSchedulerType.recurring && !(cronValue || defaultCronPeriod)) {
        return false;
      }
    }

    return true;
  }, [
    connectWebhookUrlChecker,
    cronValue,
    datetime,
    defaultCronPeriod,
    eventSubscriptionNameValidator,
    flowInput,
    inputs.context,
    inputs.eventPathsMap,
    inputs.eventTemplate,
    inputs.eventType,
    inputs.flowInput,
    inputs.job,
    inputs.name,
    inputs.source,
    inputs.targetType,
    inputs.url,
    isEventScheduler,
    notEmptyValidator,
    schedulerType,
    targetType,
    urlValidator,
    validateFields,
  ]);

  // Submit the form to the backend
  const formSubmission = useCallback(async () => {
    setRequest(true);

    const shouldCreate = subscriptionId === undefined;

    // Prepare toast props
    const successToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      title: subscriptionId ? labels.toastUpdateSuccessTitle : labels.toastCreateSuccessTitle,
      toastType: TOAST_TYPE_SUCCESS,
    };

    const errorToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      title: subscriptionId ? labels.toastUpdateFailTitle : labels.toastCreateFailTitle,
      toastType: TOAST_TYPE_DANGER,
    };

    if (!subscriptionId) {
      successToastProps.toastContent = (
        <span>
          {labels.toastCreateSuccessContent1}
          <i>{name}</i>
          {labels.toastCreateSuccessContent2}
        </span>
      );

      errorToastProps.toastContent = (
        <span>
          {labels.toastCreateFailContent1}
          <i>{name}</i>
          {labels.toastCreateFailContent2}
        </span>
      );
    }

    const handleError = (): void => {
      updateActiveToast(null);
      updateActiveToast(<Toast {...errorToastProps} />);
      setSubmit(false);
      setRequest(false);
    };

    const createSchedule = async (
      scheduleName: string,
      detailType: string,
    ): Promise<EntityEventScheduleCreated | null> => {
      let scheduleTime: string;

      if (schedulerType === EventSchedulerType.once) {
        // Get UTC time in the following format (compatible with AWS EventBridge)
        // YYYY-MM-DDTHH:mm:ss
        scheduleTime = new Date((datetime as number) - new Date().getTimezoneOffset() * 60 * 1000)
          .toISOString()
          .slice(0, -5);
      } else {
        let submittedCronValue = cronValue;
        if (defaultCronPeriod && !submittedCronValue) {
          submittedCronValue = '* * * * *';
        }

        // Service requires valid 6-field expression
        const cronExp5Split = submittedCronValue.split(' ');
        cronExp5Split[4] = '?';

        // Service has an extra 'year' field
        cronExp5Split.push('*');

        scheduleTime = cronExp5Split.join(' ');
      }

      const dto: EventBusCreateScheduleDto = {
        detailType,
        scheduleName,
        scheduleTime,
        scheduleType: schedulerType,
        tenantId: tenant.tenantReferenceId,
      };

      return await lastValueFrom(eventBusService.createEventSchedule(dto));
    };

    const deleteSchedule = async (scheduleName: string): Promise<void> => {
      try {
        await lastValueFrom(eventBusService.deleteEventSchedule({ scheduleName: scheduleName }));
      } catch (err) {
        if ((err as AjaxError).status === 404) {
          return;
        }

        throw err;
      }
    };

    const updateSchedule = async (scheduleName: string, detailType: string): Promise<void> => {
      await deleteSchedule(scheduleName);
      await createSchedule(scheduleName, detailType);
    };

    const createRule = async (detailType?: string): Promise<EntityEventSubscriptionRuleCreated | null> => {
      const dto: EventBusCreateSubscriptionRuleDto = {
        name,
        pattern: {
          action: isEventScheduler && detailType ? detailType : (eventType as string),
          ...(attributeFilters.length ? { event: convertToEventPattern(attributeFilters) } : {}),
          source: source as string,
        },
        tenantId: tenant.tenantReferenceId,
      };

      return await lastValueFrom(eventBusService.createEventSubscriptionRule(dto));
    };

    const updateRule = async (subscriptionId: string): Promise<EntityEventSubscriptionRuleUpdated | null> => {
      const dto: EventBusUpdateSubscriptionRuleDto = {
        name,
        pattern: {
          action: eventType as string,
          ...(attributeFilters.length ? { event: convertToEventPattern(attributeFilters) } : {}),
          source: source as string,
        },
        subscriptionId,
        tenantId: tenant.tenantReferenceId,
      };

      return await lastValueFrom(eventBusService.updateEventSubscriptionRule(dto));
    };

    const createTarget = async (subscriptionId: string): Promise<void> => {
      try {
        let dto: EventBusCreateEventSubscriptionTargetDto;

        if (
          targetType === EventSubscriptionTargetType.Api ||
          targetType === EventSubscriptionTargetType.IntegrationsFlow
        ) {
          dto = {
            details: {
              key: inputs.apiKey.value || DUMMY_KEY_NO_AUTH_REQUIRED,
              url: inputs.url.value,
            },
            name: window.crypto.randomUUID().replace(/-/g, '').slice(0, 20),
            subscriptionId,
            tenantId: tenant.tenantReferenceId,
            type: EventBusSubscriptionTargetType.Api,
          };

          if (!isEventScheduler && isEventTransformerRequired()) {
            dto.inputTransformer = {
              InputPathsMap: JSON.parse(inputs.eventPathsMap.value),
              InputTemplate: JSON.stringify(inputs.eventTemplate.value),
            };
          }

          if (isEventScheduler && flowInput) {
            dto.inputTransformer = {
              InputPathsMap: {},
              InputTemplate: flowInput,
            };
          }

          await lastValueFrom(eventBusService.createEventSubscriptionTarget(dto));
        } else if (targetType === EventSubscriptionTargetType.Job) {
          dto = {
            details: {
              // TODO: https://squizgroup.atlassian.net/browse/INTEGRATE-122
              key: 'some_key',
              url: getJobRunnerTarget(),
            },
            name: window.crypto.randomUUID().replace(/-/g, '').slice(0, 20),
            subscriptionId,
            tenantId: tenant.tenantReferenceId,
            type: EventBusSubscriptionTargetType.Job,
          };

          const beginJobExecutionDto: JobRunnerServiceBeginExecutionDto = {
            context: selectedContext as string,
            input: {},
            jobName: selectedJob as string,
          };

          if (!isEventScheduler && isEventTransformerRequired()) {
            beginJobExecutionDto.input = JSON.parse(inputs.eventTemplate.value);

            dto.inputTransformer = {
              InputPathsMap: JSON.parse(inputs.eventPathsMap.value),
              InputTemplate: JSON.stringify(beginJobExecutionDto),
            };
          } else {
            if (isEventScheduler && jobInputData) {
              beginJobExecutionDto.input = jobInputData;
            }

            dto.inputTransformer = {
              InputPathsMap: {},
              InputTemplate: JSON.stringify(beginJobExecutionDto),
            };
          }

          await lastValueFrom(eventBusService.createEventSubscriptionTarget(dto));
        }
      } catch (err) {
        // Clean up created subscription rule
        await lastValueFrom(
          eventBusService.deleteEventSubscriptionRule({
            subscriptionId,
            tenantId: tenant.tenantReferenceId,
          }),
        );

        // Default to parent error handling
        throw err;
      }
    };

    const updateTarget = async (subscriptionId: string, name: string): Promise<void> => {
      let dto: EventBusUpdateEventSubscriptionTargetDto;

      if (
        targetType === EventSubscriptionTargetType.Api ||
        targetType === EventSubscriptionTargetType.IntegrationsFlow
      ) {
        dto = {
          details: {
            key: inputs.apiKey.value || DUMMY_KEY_NO_AUTH_REQUIRED,
            url: inputs.url.value,
          },
          name,
          subscriptionId,
          tenantId: tenant.tenantReferenceId,
          type: EventBusSubscriptionTargetType.Api,
        };

        if (!isEventScheduler && isEventTransformerRequired()) {
          dto.inputTransformer = {
            InputPathsMap: JSON.parse(inputs.eventPathsMap.value),
            InputTemplate: JSON.stringify(JSON.parse(inputs.eventTemplate.value)),
          };
        }

        if (isEventScheduler && flowInput) {
          dto.inputTransformer = {
            InputPathsMap: {},
            InputTemplate: flowInput,
          };
        }

        await lastValueFrom(eventBusService.updateEventSubscriptionTarget(dto));
      } else if (targetType === EventSubscriptionTargetType.Job) {
        dto = {
          details: {
            // TODO: https://squizgroup.atlassian.net/browse/INTEGRATE-122
            key: 'some_key',
            url: getJobRunnerTarget(),
          },
          name,
          subscriptionId,
          tenantId: tenant.tenantReferenceId,
          type: EventBusSubscriptionTargetType.Job,
        };

        const beginJobExecutionDto: JobRunnerServiceBeginExecutionDto = {
          context: selectedContext as string,
          input: {},
          jobName: selectedJob as string,
        };

        if (!isEventScheduler && isEventTransformerRequired()) {
          beginJobExecutionDto.input = JSON.parse(inputs.eventTemplate.value);

          dto.inputTransformer = {
            InputPathsMap: JSON.parse(inputs.eventPathsMap.value),
            InputTemplate: JSON.stringify(beginJobExecutionDto),
          };
        } else {
          if (isEventScheduler && jobInputData) {
            beginJobExecutionDto.input = jobInputData;
          }

          dto.inputTransformer = {
            InputPathsMap: {},
            InputTemplate: JSON.stringify(beginJobExecutionDto),
          };
        }

        await lastValueFrom(eventBusService.updateEventSubscriptionTarget(dto));
      }
    };

    try {
      if (shouldCreate) {
        // Create event subscription rule
        const detailType = isEventScheduler ? window.crypto.randomUUID() : undefined;
        const subscriptionResult = await createRule(detailType);
        const { subscriptionid } = subscriptionResult as EntityEventSubscriptionRuleCreated;

        await createTarget(subscriptionid);

        if (isEventScheduler && detailType) {
          await createSchedule(subscriptionid, detailType);
        }
      } else {
        await updateRule(subscriptionId);

        if (
          targetType === EventSubscriptionTargetType.Job ||
          (targetType === EventSubscriptionTargetType.IntegrationsFlow &&
            isEventScheduler &&
            flowInput &&
            !isPasswordField) ||
          !isPasswordField
        ) {
          await updateTarget(subscriptionId, targetName);
        }

        if (isEventScheduler && eventType) {
          await updateSchedule(subscriptionId, eventType);
        }
      }

      updateActiveToast(null);
      updateActiveToast(<Toast {...successToastProps} />);

      setSubmit(false);
      returnToEventSubscriptionsList();
    } catch (err) {
      handleError();
    }
  }, [
    attributeFilters,
    cronValue,
    datetime,
    defaultCronPeriod,
    eventBusService,
    eventType,
    flowInput,
    inputs.apiKey,
    inputs.eventPathsMap,
    inputs.eventTemplate,
    inputs.url,
    isEventScheduler,
    isEventTransformerRequired,
    isPasswordField,
    jobInputData,
    labels.toastCreateFailContent1,
    labels.toastCreateFailContent2,
    labels.toastCreateFailTitle,
    labels.toastCreateSuccessContent1,
    labels.toastCreateSuccessContent2,
    labels.toastCreateSuccessTitle,
    labels.toastUpdateFailTitle,
    labels.toastUpdateSuccessTitle,
    name,
    returnToEventSubscriptionsList,
    schedulerType,
    selectedContext,
    selectedJob,
    source,
    subscriptionId,
    targetName,
    targetType,
    tenant.tenantReferenceId,
    updateActiveToast,
  ]);

  // JsonEditor validation
  const jobInputValidationHandler = useCallback(
    (errors: Array<ValidationError>): void => {
      if (errors.length > 0) {
        setValidate(false);
        setSubmit(false);
      } else if (!isFormError) {
        formSubmission();
      }
    },
    [formSubmission, isFormError, setSubmit, setValidate],
  );

  //--------------------
  // Use effect
  //--------------------

  // Pre-fill scheduled event data
  useEffect(() => {
    if (subscriptionId && eventSubscription && eventSchedule) {
      const { pattern } = eventSubscription;
      const { source } = pattern;

      /* istanbul ignore next */
      if ((source[0] as EventSubscriptionSourceAll) !== EventSubscriptionSourceAll.EventScheduler) {
        return;
      }

      const { scheduleExpression } = eventSchedule;

      if (scheduleExpression.startsWith('at(')) {
        // YYYY-MM-DDTHH:mm:ss
        const formattedDatetime = scheduleExpression.slice(3, -1);
        const formattedDate = new Date(`${formattedDatetime}Z`);
        // Set value such that the datetime picker displays the time as UTC
        setDatetime(formattedDate.getTime() + formattedDate.getTimezoneOffset() * 60 * 1000);
      }

      if (scheduleExpression.startsWith('cron(')) {
        const expression = scheduleExpression.slice(5, -1);
        const expressionParts = expression.split(' ');
        if (expressionParts[expressionParts.length - 2] === '?') {
          expressionParts[expressionParts.length - 2] = '*';
        }

        // Drop the year expression part
        setCronValue(expressionParts.join(' ').slice(0, -2));
      }

      const target = eventSubscription.targets && eventSubscription.targets[0];
      if (target) {
        const isJobTargetType =
          target.details.url.includes(`${JOB_RUNNER_URL_PATH}`) &&
          target.details.url.includes(`${JOB_RUNNER_EXECUTIONS_ENDPOINT}`);
        const isConnectTargetType = connectWebhookUrlChecker(target.details.url);

        const { inputTransformer } = target;

        if (isJobTargetType) {
          setJobInputData(JSON.parse(inputTransformer.InputTemplate).input);
          return;
        }

        if (isConnectTargetType) {
          if (inputTransformer.InputTemplate === DEFAULT_EVENT_TRANSFORMER_EVENT_TEMPLATE_STRING) {
            setFlowInput('');
          } else {
            setFlowInput(JSON.stringify(JSON.parse(inputTransformer.InputTemplate), null, 2));
          }
        }
      }
    }
  }, [connectWebhookUrlChecker, eventBusService, eventSchedule, eventSubscription, subscriptionId]);

  // Pre-fill form for editing subscription
  useEffect(() => {
    if (!isLoaded && subscriptionId && eventSubscription) {
      setLoaded(true);

      const { name, pattern, targets } = eventSubscription;
      const { action, event, source } = pattern;

      setName(name);
      setSource(source[0] as EventSubscriptionSource);
      setEventType(action[0] as EventSubscriptionEventType);

      // Attribute filters
      const mappedToAttributeFilters = Object.keys(event).map((attribute) => {
        const partialAttributeFilter =
          convertToAttributeFilter(attribute, event[attribute] as EventRuleSyntax) ?? getDefaultAttributeFilter();
        return {
          ...partialAttributeFilter,
          id: window.crypto.randomUUID(),
        };
      });
      setAttributeFilters(mappedToAttributeFilters);

      if (targets) {
        const subscriptionTarget = targets[0];

        // Event subscriptions all have 'api' targets in the backend. But the event service
        // determines the target to be type 'job' target type will be determined by the target URL.
        const isJobTargetType =
          subscriptionTarget.details.url.includes(`${JOB_RUNNER_URL_PATH}`) &&
          subscriptionTarget.details.url.includes(`${JOB_RUNNER_EXECUTIONS_ENDPOINT}`);

        const isConnectTargetType = connectWebhookUrlChecker(subscriptionTarget.details.url);

        if (isJobTargetType) {
          setTargetType(EventSubscriptionTargetType.Job);
          const inputTemplateObject = JSON.parse(subscriptionTarget.inputTransformer.InputTemplate);
          setSelectedContext(inputTemplateObject.context);
          setSelectedJob(inputTemplateObject.jobName);
        } else if (isConnectTargetType) {
          setTargetType(EventSubscriptionTargetType.IntegrationsFlow);
          setTargetUrl(subscriptionTarget.details.url);
        } else {
          setTargetType(EventSubscriptionTargetType.Api);
          setTargetUrl(subscriptionTarget.details.url);
        }

        if (subscriptionTarget.details.url) {
          // Set textarea row size to 0 to autofit the url that's being loaded
          setDefaultRowSize(true);
        }

        const { InputPathsMap: inputPathsMap, InputTemplate: inputTemplate } = subscriptionTarget.inputTransformer;

        const isDefaultEventPathsMap =
          JSON.stringify(inputPathsMap) === JSON.stringify(DEFAULT_EVENT_TRANSFORMER_EVENT_PATHS_MAP);
        if (!isEventScheduler && !isDefaultEventPathsMap) {
          setEventPathsMap(JSON.stringify(inputPathsMap, null, 2));
        }

        const isDefaultEventTemplate = inputTemplate === DEFAULT_EVENT_TRANSFORMER_EVENT_TEMPLATE_STRING;
        if (!isEventScheduler && !isDefaultEventTemplate) {
          if (isJobTargetType) {
            if (Object.keys(JSON.parse(inputTemplate).input).length === 0) {
              setEventTemplate('');
              setEventPathsMap('');
            } else {
              setEventTemplate(JSON.stringify(JSON.parse(inputTemplate).input));
            }
          } else {
            setEventTemplate(JSON.stringify(JSON.parse(inputTemplate), null, 2));
          }
        }
      }
    }
  }, [
    connectWebhookUrlChecker,
    eventSubscription,
    getDefaultAttributeFilter,
    isEventScheduler,
    isLoaded,
    subscriptionId,
  ]);

  // Reset event type select field if source changes
  useEffect(() => {
    if (!source) {
      return;
    }

    const isEventScheduler = source === EventSubscriptionSourceAll.EventScheduler;
    setEventScheduler(isEventScheduler);

    // Event type is not rendered for scheduled events
    if (source && !isEventScheduler && eventType) {
      const isCompatible =
        EVENT_TYPE_OPTIONS[source] && EVENT_TYPE_OPTIONS[source].find((option) => option.value === eventType);
      if (!isCompatible) {
        setEventType(null);
      }
    }
  }, [eventType, source]);

  // Fetch job and contexts data for event subscriptions targeting jobs
  useEffect(() => {
    if (targetType === EventSubscriptionTargetType.Job) {
      fetchJobsAndContexts();
    }
  }, [fetchJobsAndContexts, targetType]);

  // Fetch manifest of selected job for determining job inputs
  useEffect(() => {
    if (!selectedJob) {
      return;
    }

    if (!jobList || !jobList.length) {
      return;
    }

    const getJobManifest = async (): Promise<void> => {
      const jobObject = jobList.find((job) => job.name === selectedJob);
      if (!jobObject) {
        return;
      }

      const { version } = jobObject;

      const jobManifestFound = await lastValueFrom(
        jobRunnerService.getJobManifest({
          name: selectedJob,
          version,
        }),
      );

      setJobManifest(jobManifestFound);
    };

    getJobManifest();
  }, [jobList, jobRunnerService, selectedJob]);

  // Clear cron editor error message
  /* istanbul ignore next */
  useEffect(() => {
    if (cronValue && fieldErrors.cron) {
      const nextErrors = { ...fieldErrors };
      delete nextErrors.cron;

      setFieldErrors(nextErrors);
    }
  }, [cronValue, fieldErrors]);

  // Validate form fields and JSONEditor at the same time before submitting the form
  useEffect(() => {
    if (isFormSubmitted && !isMakingRequest) {
      const needsJobInputValidation =
        isEventScheduler &&
        targetType === EventSubscriptionTargetType.Job &&
        jobManifest &&
        jobManifest.entryFunction.input.properties
          ? true
          : false;

      const isValid = validateSubmission();
      setFormError(!isValid);

      if (needsJobInputValidation) {
        setValidate(true);
      } else if (isValid) {
        formSubmission();
      } else {
        setSubmit(false);
      }
    }
  }, [formSubmission, isEventScheduler, isFormSubmitted, isMakingRequest, jobManifest, targetType, validateSubmission]);

  //--------------------
  // Rendering
  //--------------------

  const contextOptions = useMemo((): Array<SelectFieldOption> => {
    return (contextList || []).map((context) => {
      return {
        key: context.contextName,
        label: context.contextName,
        value: context.contextName,
      };
    });
  }, [contextList]);

  const jobOptions = useMemo((): Array<SelectFieldOption> => {
    return (jobList || []).map((job) => {
      return {
        key: job.name,
        label: job.name,
        value: job.name,
      };
    });
  }, [jobList]);

  const getSubscriptionDetails = (): JSX.Element => {
    const subscriptionNameFieldProps: TextFieldProps = {
      customChange: (event: ChangeEvent<HTMLInputElement>): void => {
        errorChecker(event);
        setName(event.target.value);
      },
      errorMessage: fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionName],
      fieldLabel: labels.nameRequired,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionName,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionName,
      value: name,
    };

    const sourceFieldProps: SelectFieldProps = {
      callback: (option) => {
        selectErrorChecker(EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionSource);
        setSource(option.value as EventSubscriptionSource);
      },
      errorMessage: fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionSource],
      fieldLabel: labels.sourceRequired,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionSource,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionSource,
      note: labels.sourceNote,
      options: SUBSCRIPTION_SOURCE_OPTIONS,
      value: source,
    };

    const eventTypeFieldProps: SelectFieldProps = {
      callback: (option): void => {
        selectErrorChecker(EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionEventType);
        setEventType(option.value as EventSubscriptionEventType);
      },
      classes: `event-subscription-builder__event-type`,
      disabled: !source || !Object.values(EventSubscriptionSource).includes(source as EventSubscriptionSource),
      errorMessage: fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionEventType],
      fieldLabel: labels.eventTypeRequired,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionEventType,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.subscriptionEventType,
      note: labels.eventTypeNote,
      options:
        source && source !== EventSubscriptionSourceAll.EventScheduler && EVENT_TYPE_OPTIONS[source]
          ? EVENT_TYPE_OPTIONS[source]
          : [],
      value: eventType ?? '',
    };

    return (
      <>
        <div className="event-subscription-builder__subscription-header">
          <h2>{labels.subscriptionDetails}</h2>
        </div>
        <Row data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.subscriptionDetails}>
          <div className="event-subscription-builder__subscription-details-fields">
            <TextField
              {...subscriptionNameFieldProps}
              data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.subscriptionName}
              ref={nameInputRef}
            />
            <SelectField
              {...sourceFieldProps}
              data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.subscriptionSource}
              ref={sourceInputRef}
            />
            {!isEventScheduler && (
              <SelectField
                {...eventTypeFieldProps}
                data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.eventType}
                ref={eventTypeInputRef}
              />
            )}
          </div>
          {!isEventScheduler && (
            <EventSubscriptionAttributes
              attributeFilters={attributeFilters}
              eventType={eventType}
              fieldErrors={fieldErrors}
              setAttributeFilters={setAttributeFilters}
              setFieldErrors={setFieldErrors}
            />
          )}
        </Row>
      </>
    );
  };

  const getApiTargetTypeDetails = (): JSX.Element => {
    const urlFieldProps: TextFieldProps = {
      customChange: (event: ChangeEvent<HTMLInputElement>): void => {
        if (isDefaultRowSize) {
          setDefaultRowSize(false);
        }
        if (isPasswordField) {
          setTargetApiKey('');
          setPasswordField(false);
        }
        errorChecker(event);
        setTargetUrl(event.target.value.trim());
      },

      errorMessage: fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.url],
      fieldLabel: labels.urlRequired,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.url,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.url,
      rows: 1,
      type: TEXTFIELD_TYPE_MULTIPLE,
      value: targetUrl,
    };

    const apiKeyFieldProps: TextFieldProps = {
      customChange: (event: ChangeEvent<HTMLInputElement>): void => {
        setTargetApiKey(event.target.value);
      },

      errorMessage: fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.apiKey],
      fieldLabel: labels.apiKey,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.apiKey,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.apiKey,
      onClick: (): void => {
        if (isPasswordField) {
          setTargetApiKey('');
          setPasswordField(false);
        }
      },
      ...(isPasswordField ? { type: TEXTFIELD_TYPE_PASSWORD } : {}),
      value: isPasswordField ? window.crypto.randomUUID().replace(/./g, '*') : targetApiKey,
    };

    return (
      <>
        <div className="event-subscription__target-details">
          <h3>{labels.apiKeyDetails}</h3>
          <div className="event-subscription-target__fields">
            <TextField {...urlFieldProps} data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.urlRequired} ref={urlInputRef} />
            <TextField
              {...apiKeyFieldProps}
              data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.apiKey}
              ref={apiKeyInputRef}
            />
          </div>
        </div>
        {!isEventScheduler && eventTransformerSection()}
      </>
    );
  };

  const getIntegrationsTargetTypeDetails = (): JSX.Element => {
    const urlFieldProps: TextFieldProps = {
      customChange: (event: ChangeEvent<HTMLInputElement>): void => {
        if (isDefaultRowSize) {
          setDefaultRowSize(false);
        }
        if (isPasswordField) {
          setTargetApiKey('');
          setPasswordField(false);
        }
        errorChecker(event);
        setTargetUrl(event.target.value.trim());
      },
      errorMessage: fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.url],
      fieldLabel: labels.flowWebhookUrlRequired,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.url,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.url,
      note: labels.integrationsFlowTargetUrlHelperText,
      rows: 1,
      type: TEXTFIELD_TYPE_MULTIPLE,
      value: targetUrl,
    };

    const apiKeyFieldProps: TextFieldProps = {
      customChange: (event: ChangeEvent<HTMLInputElement>): void => {
        setTargetApiKey(event.target.value);
      },

      errorMessage: fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.apiKey],
      fieldLabel: labels.apiKey,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.apiKey,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.apiKey,
      onClick: (): void => {
        if (isPasswordField) {
          setTargetApiKey('');
          setPasswordField(false);
        }
      },
      ...(isPasswordField ? { type: TEXTFIELD_TYPE_PASSWORD } : {}),
      value: isPasswordField ? window.crypto.randomUUID().replace(/./g, '*') : targetApiKey,
    };

    const message = isEventScheduler
      ? labels.integrationsDetailsScheduledAlertMessage
      : labels.integrationsDetailsAlertMessage;

    const getFlowInputFields = (): JSX.Element => {
      return (
        <div className="event-subscription-target__flow-input">
          <h3>{labels.flowInput}</h3>
          <p>{labels.flowInputDescription}</p>
          <div className="event-subscription-target__flow-input-container">
            <TextField
              classes="event-subscription-target__flow-input-field"
              customChange={(event: ChangeEvent<HTMLInputElement>): void => {
                if (isPasswordField) {
                  setTargetApiKey('');
                  setPasswordField(false);
                }
                validateFields([flowInputRef.current as HTMLInputElement]);
                setFlowInput(event.target.value);
              }}
              errorMessage={fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.flowInput]}
              fieldLabel={labels.jsonObjectInput}
              id={EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.flowInput}
              name={EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.flowInput}
              note={labels.jsonFormatNote}
              placeholder={labels.flowInputPlaceholder}
              ref={flowInputRef}
              type={TEXTFIELD_TYPE_TEXTAREA}
              value={flowInput}
            />
          </div>
        </div>
      );
    };

    return (
      <>
        <div className="event-subscription__target-details">
          <h3>{labels.integrationsDetails}</h3>
          <Alert
            classes="event-subscription__job-target-alert"
            hasBgColor
            isDark
            message={message}
            type={ALERT_TYPE_INFO}
          />
          <div className="event-subscription-target__fields">
            <TextField {...urlFieldProps} data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.urlRequired} ref={urlInputRef} />
            <TextField
              {...apiKeyFieldProps}
              data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.apiKey}
              ref={apiKeyInputRef}
            />
          </div>
        </div>
        {!isEventScheduler ? eventTransformerSection() : getFlowInputFields()}
      </>
    );
  };

  const getJobTargetTypeDetails = (): JSX.Element => {
    const jobNameFieldProps: SelectFieldProps = {
      callback: (option) => {
        selectErrorChecker(EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.jobName);
        if (isEventScheduler) {
          setJobManifest(null);
        }

        setSelectedJob(option.value);
      },
      errorMessage: fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.jobName],
      fieldLabel: labels.nameRequired,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.jobName,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.jobName,
      options: jobOptions,
      value: selectedJob,
    };

    const contextNameFieldProps: SelectFieldProps = {
      callback: (option) => {
        selectErrorChecker(EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.contextName);
        setSelectedContext(option.value);
      },
      errorMessage: fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.contextName],
      fieldLabel: labels.context,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.contextName,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.contextName,
      options: contextOptions,
      value: selectedContext,
    };

    const alertProps: AlertProps = {
      classes: 'event-subscription__jobs-contexts-fetch-error-alert',
      hasBgColor: true,
      message: (
        <span>
          {labels.errorMessageWhenJobOrContextFetchFails}
          {
            <Button
              className="event-subscription__fetch-jobs-contexts-try-again__button"
              dataId={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.tryAgainButton}
              format={BUTTON_FORMAT_TRANSPARENT}
              onClick={(): Promise<void> => fetchJobsAndContexts()}
              text={labels.tryAgain}
              theme={BUTTON_THEME_DEFAULT}
            />
          }
        </span>
      ),
      type: ALERT_TYPE_REPORT,
    };

    // waiting for job runner API results and there are no errors
    if ((!jobList || !contextList) && !errorFetchingContexts && !errorFetchingJobs) {
      return (
        <div className="loading event-subscription__fetch-job-contexts-loader">
          <Spinner type={SPINNER_TYPE_REGULAR} />
        </div>
      );
    }

    // when job runner API throws error
    if (errorFetchingContexts || errorFetchingJobs) {
      return <Alert {...alertProps} />;
    }

    const alertMessage = isEventScheduler ? labels.jobDetailsScheduledAlertMessage : labels.jobDetailsAlertMessage;

    const getJobInputFields = (): JSX.Element => {
      if (!selectedJob) {
        return <></>;
      }

      if (!jobManifest) {
        return (
          <div className="loading">
            <Spinner type={SPINNER_TYPE_REGULAR} />
          </div>
        );
      }

      // Determines if JSON Editor will be rendered
      const isInputRequired = jobManifest && jobManifest.entryFunction.input.properties ? true : false;
      if (!isInputRequired) {
        return <></>;
      }

      // Set up JSON Editor implementation
      const jobInputSchema: JobManifestInputSchema = {
        ...jobManifest.entryFunction.input,
        title: ``,
      };

      const editorProps: JsonEditorProps = {
        data: jobInputData as EventScheduledJobInputFormType,
        onValidate: jobInputValidationHandler,
        schema: jobInputSchema as Schema,
        setData: setJobInputData as JsonEditorProps['setData'],
        showDescriptions: false,
        showHeader: false,
        showSubmitButton: false,
        validate,
      };

      return (
        <div className="event-subscription-target__job-input">
          <h3>{labels.jobInputs}</h3>
          <p>{labels.jobInputsDescription}</p>
          <JsonEditor {...editorProps} />
        </div>
      );
    };

    return (
      <>
        <div className="event-subscription__target-details">
          <h3>{labels.jobDetails}</h3>
          <Alert
            classes="event-subscription__job-target-alert"
            hasBgColor
            isDark
            message={alertMessage}
            type={ALERT_TYPE_INFO}
          />
          <div className="event-subscription-target__fields">
            <SelectField
              {...jobNameFieldProps}
              data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.jobName}
              ref={jobNameInputRef}
            />
            <SelectField
              {...contextNameFieldProps}
              data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.contextName}
              ref={contextNameInputRef}
            />
          </div>
        </div>
        <div>{getJobInputFields()}</div>
        {!isEventScheduler && eventTransformerSection()}
      </>
    );
  };

  const eventTransformerSection = (): JSX.Element => {
    return (
      <div className="event-subscription-target__event-transformer">
        <h3>{labels.eventTransformerOptional}</h3>
        <p>{targetType === EventSubscriptionTargetType.Api && labels.apiEventTransformerDescription}</p>
        <p>{targetType === EventSubscriptionTargetType.Job && labels.jobEventTransformerDescription}</p>
        <p>
          {targetType === EventSubscriptionTargetType.IntegrationsFlow &&
            labels.integrationsEventTransformerDescription}
        </p>
        <div className="event-subscription-target__event-transformer-fields">
          <TextField
            customChange={(event: ChangeEvent<HTMLInputElement>): void => {
              if (isPasswordField) {
                setTargetApiKey('');
                setPasswordField(false);
              }
              eventTransformerErrorChecker();
              setEventPathsMap(event.target.value);
            }}
            errorMessage={fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.eventPathsMap]}
            fieldLabel={labels.eventPathsMap}
            id={EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.eventPathsMap}
            name={EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.eventPathsMap}
            note={labels.jsonFormatNote}
            placeholder={labels.eventPathsMapPlaceholder}
            ref={eventPathsMapInputRef}
            type={TEXTFIELD_TYPE_TEXTAREA}
            value={eventPathsMap}
          />
          <TextField
            customChange={(event: ChangeEvent<HTMLInputElement>): void => {
              if (isPasswordField) {
                setTargetApiKey('');
                setPasswordField(false);
              }
              eventTransformerErrorChecker();
              setEventTemplate(event.target.value);
            }}
            errorMessage={fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.eventTemplate]}
            fieldLabel={labels.eventTemplate}
            id={EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.eventTemplate}
            name={EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.eventTemplate}
            note={labels.jsonFormatNote}
            placeholder={labels.eventTemplatePlaceholder}
            ref={eventTemplateInputRef}
            type={TEXTFIELD_TYPE_TEXTAREA}
            value={eventTemplate}
          />
        </div>
      </div>
    );
  };

  const getTargetDetails = (): JSX.Element => {
    const targetTypeFieldProps: SelectFieldProps = {
      callback: (option) => {
        selectErrorChecker(EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.targetType);
        setTargetType(option.value);
        setSelectedJob('');
        setSelectedContext('');
        setTargetApiKey('');
        setPasswordField(false);
        setTargetUrl('');
      },

      errorMessage: fieldErrors[EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.targetType],
      fieldLabel: labels.targetTypeRequired,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.targetType,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.targetType,
      options: !isEventScheduler
        ? EVENT_SUBSCRIPTION_TARGET_TYPE_OPTIONS
        : EVENT_SUBSCRIPTION_TARGET_TYPE_SCHEDULER_OPTIONS,
      value: targetType,
    };

    const description = isEventScheduler ? labels.targetDescriptionScheduled : labels.targetDescription;
    return (
      <>
        <h2>{labels.targetDetails}</h2>
        <Row data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.targetDetails}>
          <p>{description}</p>
          <div className="event-subscription-target-type__wrapper">
            <SelectField
              {...targetTypeFieldProps}
              data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.targetType}
              ref={targetTypeInputRef}
            />
          </div>
          {targetType === EventSubscriptionTargetType.Api && getApiTargetTypeDetails()}
          {targetType === EventSubscriptionTargetType.Job && getJobTargetTypeDetails()}
          {targetType === EventSubscriptionTargetType.IntegrationsFlow && getIntegrationsTargetTypeDetails()}
        </Row>
      </>
    );
  };

  const getSchedulerDetails = (): JSX.Element => {
    const renderDatetime = (): JSX.Element => {
      const datetimePickerProps: DatetimePickerProps = {
        callback: handleDatetime,
        format: PICKER_DATETIME_FORMAT,
        id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.dateTimePicker,
        placeholder: '',
        selectedDate: datetime ? datetime.toString() : undefined,
        type: 'SINGLE',
      };

      const errorMessage = fieldErrors.datetime;

      return (
        <>
          <div className="event-subscription__datetime-picker-container">
            <label className="sds-form-label" htmlFor={EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.dateTimePicker}>
              {labels.utcDateAndTimeRequired}
            </label>
            <DatetimePicker {...datetimePickerProps} />
          </div>
          {errorMessage && (
            <div className="sds-field-error__message">
              <span>{errorMessage}</span>
            </div>
          )}
        </>
      );
    };

    const renderCron = (): JSX.Element => {
      const cronEditorProps: CronEditorProps = {
        defaultPeriod: defaultCronPeriod,
        setDefaultPeriod: setCron,
        setValue: setCronValue,
        value: cronValue,
      };

      const errorMessage = fieldErrors.cron;

      return (
        <>
          <CronEditor {...cronEditorProps} />
          {!defaultCronPeriod && errorMessage && (
            <div className="sds-field-error__message">
              <span>{errorMessage}</span>
            </div>
          )}
        </>
      );
    };

    const renderScheduler = (): JSX.Element => {
      return (
        <>
          {schedulerType === EventSchedulerType.once && renderDatetime()}
          {schedulerType === EventSchedulerType.recurring && renderCron()}
        </>
      );
    };

    const schedulerButtonProps: CheckboxRadioProps = {
      classes: 'event-subscription__scheduler-type',
      fieldLabel: labels.schedulerType,
      hideLabel: true,
      id: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.schedulerType,
      name: EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.schedulerType,
      options: EVENT_SCHEDULER_TYPE_OPTIONS,
      radioCallback: (option: FormControlOption) => {
        setSchedulerType(option.value as EventSchedulerType);
      },
      required: true,
      value: schedulerType,
    };

    const schedulerDescription =
      schedulerType === EventSchedulerType.once
        ? labels.schedulerOnceDescription
        : labels.schedulerRecurringDescription;

    const utcAlertProps: AlertProps = {
      classes: 'event-subscription__utc-alert',
      hasBgColor: true,
      message: labels.utcAlertMessage,
      type: ALERT_TYPE_INFO,
    };

    return (
      <div className="event-subscription-builder__scheduler">
        <h2>{labels.scheduler}</h2>
        <Row data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.scheduler}>
          <RadioButtons {...schedulerButtonProps} ref={schedulerButtonsRef} />
          <p>{schedulerDescription}</p>
          <Alert {...utcAlertProps} />
          {renderScheduler()}
        </Row>
      </div>
    );
  };

  const getFooterButtons = (): JSX.Element => {
    const cancelButton = (
      <div className="event-subscription-builder-form__footer-cancel-button-wrapper">
        <Button
          data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.cancelButton}
          format={BUTTON_FORMAT_SOFT}
          onClick={(): void => returnToEventSubscriptionsList()}
          text={labels.cancel}
          theme={BUTTON_THEME_DEFAULT}
        />
      </div>
    );

    const submitButtonDataId = subscriptionId
      ? EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.saveButton
      : EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.createButton;
    const submitButtonLabel = subscriptionId ? labels.save : labels.create;

    const submitButton = (
      <div className="event-subscription-builder-form__footer-submit-button-wrapper">
        <Button
          data-id={submitButtonDataId}
          isLoading={isFormSubmitted}
          onClick={(): void => {
            setSubmit(true);
          }}
          text={submitButtonLabel}
          theme={BUTTON_THEME_SUCCESS}
        />
      </div>
    );

    const buttonList = [];
    const buttonKeys = [];

    const submitButtonKey = subscriptionId ? `save` : `create`;

    if (!isFormSubmitted && !subscriptionId) {
      buttonKeys.push(`cancel`);
      buttonList.push(cancelButton);
    }

    buttonKeys.push(submitButtonKey);
    buttonList.push(submitButton);

    return (
      <div className="event-subscription-builder-form__footer-buttons-wrapper">
        <ButtonList
          buttonKeys={buttonKeys}
          buttons={buttonList}
          classes="event-subscription-builder-form__footer-buttons-list"
        />
      </div>
    );
  };

  return (
    <>
      <div className="event-subscription-builder-form">
        {subscriptionId && eventSubscription && (
          <EventSubscriptionMenu subscriptionId={subscriptionId} subscriptionName={subscriptionName} />
        )}
        <div className="event-subscription-builder__subscription-details">{getSubscriptionDetails()}</div>
        <div className="event-subscription-builder__target-details">{getTargetDetails()}</div>
        {isEventScheduler && getSchedulerDetails()}
        {getFooterButtons()}
      </div>
    </>
  );
};
